import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  FirestoreUser
} from '../../../services/creditService';
import { 
  updateUserRole, 
  getUsersByCompany, 
  updateUserStatus 
} from '../../../services/userService';
import {
  TabSection,
  SectionTitle,
  SectionDescription,
  UserList,
  UserCard,
  UserInfo,
  UserDetails,
  UserName,
  UserEmail,
  UserRole,
  RoleSelect,
  ActionButton,
  EmptyState,
  LoadingSpinner
} from './styled';

const ManageUsersTab: React.FC = () => {
  const { companyData } = useAuth();
  const [users, setUsers] = useState<FirestoreUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingUser, setUpdatingUser] = useState<string | null>(null);

  // Get company data from AuthContext (already loaded)
  const companyId = companyData?.id;

  // Load users for the current company
  useEffect(() => {
    if (!companyId) return;

    const loadUsers = async () => {
      setLoading(true);
      try {
        // Get real users from Firestore for this company
        const firestoreUsers = await getUsersByCompany(companyId);
        setUsers(firestoreUsers);
      } catch (error) {
        console.error('Failed to load users:', error);
        // Fallback to empty array if error
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [companyId]);

  const handleRoleChange = async (userId: string, newRole: string) => {
    setUpdatingUser(userId);
    try {
      // Update user role in Firestore
      await updateUserRole(userId, newRole as 'user' | 'admin' | 'super_admin');
      
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId ? { ...user, role: newRole as 'user' | 'admin' | 'super_admin' } : user
        )
      );
    } catch (error) {
      console.error('Failed to update user role:', error);
    } finally {
      setUpdatingUser(null);
    }
  };

  const handleStatusToggle = async (userId: string) => {
    setUpdatingUser(userId);
    try {
      // Get current user status to toggle it
      const currentUserData = users.find(user => user.id === userId);
      const newStatus = currentUserData?.status === 'active' ? 'inactive' : 'active';
      
      // Update user status in Firestore
      await updateUserStatus(userId, newStatus);
      
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === userId 
            ? { ...user, status: newStatus }
            : user
        )
      );
    } catch (error) {
      console.error('Failed to update user status:', error);
    } finally {
      setUpdatingUser(null);
    }
  };

  if (!companyData) {
    return (
      <TabSection>
        <LoadingSpinner>Loading...</LoadingSpinner>
      </TabSection>
    );
  }

  if (loading) {
    return (
      <TabSection>
        <LoadingSpinner>Loading users...</LoadingSpinner>
      </TabSection>
    );
  }

  return (
    <TabSection>
      <SectionTitle>Manage Users</SectionTitle>
      <SectionDescription>
        Manage users in your organization ({companyData?.domain || 'Unknown Domain'})
      </SectionDescription>

      {users.length === 0 ? (
        <EmptyState>
          <div style={{ textAlign: 'center', color: '#64748b' }}>
            <p>No users found for your organization.</p>
            <p>Users will appear here when they join your company.</p>
          </div>
        </EmptyState>
      ) : (
        <UserList>
          {users.map((user) => (
            <UserCard key={user.id} $isActive={user.status !== 'inactive'}>
              <UserInfo>
                <UserDetails>
                  <UserName>{user.name}</UserName>
                  <UserEmail>{user.email}</UserEmail>
                  <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '4px' }}>
                    Last login: {user.lastLogin || 'Never'}
                    <br />
                    Status: {user.status || 'active'}
                  </div>
                </UserDetails>
                
                <UserRole>
                  <div style={{ marginBottom: '8px' }}>
                    <label style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '4px', display: 'block' }}>
                      Role:
                    </label>
                    <RoleSelect
                      value={user.role}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      disabled={updatingUser === user.id}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </RoleSelect>
                  </div>
                </UserRole>
              </UserInfo>
              
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <ActionButton
                  variant="secondary"
                  onClick={() => handleStatusToggle(user.id)}
                  disabled={updatingUser === user.id}
                >
                  {updatingUser === user.id ? 'Updating...' : 
                    user.status === 'active' ? 'Deactivate' : 'Activate'
                  }
                </ActionButton>
              </div>
            </UserCard>
          ))}
        </UserList>
      )}
    </TabSection>
  );
};

export default ManageUsersTab;